import type { ReactNode } from "react"
import { useEffect, useMemo, useState } from "react"
import {
	IconGlobeFill18 as Globe,
	IconImportFill18 as Import,
} from "nucleo-ui-fill-18"

import type { HelpCenterHomeSection } from "@productlane/lib/src/help-center-home-sections"
import { resolveExampleQuestions } from "@productlane/lib/src/ai-chat-example-questions"
import { HelpCenterGreeting } from "@productlane/ui"

import type { ArticleWithSubpages, GroupWithArticles } from "./types"
import { toast } from "@/components/toast"
import { useRole } from "@/lib/useRole"
import { useZeroWorkspace } from "@/lib/zero"
import { useDocNavStore } from "@/stores/docs/docs-nav-store"
import { isPublicTreeRow } from "./article-kind"
import { DocsAddTile } from "./docs-add-tile"
import {
	useHelpCenterArticlesSettled,
	useHelpCenterLanguage,
} from "./help-center-context"
import { HelpCenterHeader } from "./help-center-header"
import { HelpCenterHomeTile } from "./help-center-home-tile"
import {
	HelpCenterArticleSummaryWithTranslation,
	HelpCenterArticleTitleWithTranslation,
} from "./help-center-title-with-translation"
import { HomeCuratedSection } from "./home-curated-section"
import { HomeCustomSections } from "./home-custom-sections"
import {
	articlesInHomeSection,
	hiddenCuratedSections,
	homeSectionCandidates,
} from "./home-sections"
import { HomeTileSection } from "./home-tile-section"
import { offersDocsAddTile } from "./offers-docs-add-tile"
import { useCreateArticle } from "./use-create-article"

const MARKDOWN_IMPORT_ROUTE = "/settings/docs-markdown-import"

function VisibilitySection({
	label,
	articles,
	emptyState,
}: {
	label: string
	articles: readonly ArticleWithSubpages[]
	emptyState?: ReactNode
}) {
	const { selectedLanguage } = useHelpCenterLanguage()

	return (
		<HomeTileSection
			label={label}
			items={articles}
			emptyState={emptyState}
			renderTile={(article) => (
				<HelpCenterHomeTile
					article={article}
					key={article.id}
					titleComponent={
						<HelpCenterArticleTitleWithTranslation
							article={article}
							selectedLanguage={selectedLanguage}
						/>
					}
					summaryComponent={
						<HelpCenterArticleSummaryWithTranslation
							article={article}
							selectedLanguage={selectedLanguage}
						/>
					}
				/>
			)}
		/>
	)
}

export function HelpCenterHomeView({
	articleGroups,
}: {
	articleGroups: readonly GroupWithArticles[]
}) {
	const workspace = useZeroWorkspace()
	const { role } = useRole()
	const { selectedLanguage, setSelectedLanguage } = useHelpCenterLanguage()
	const articlesSettled = useHelpCenterArticlesSettled()
	const createArticle = useCreateArticle({ articleGroups })
	const [addedCurated, setAddedCurated] = useState<
		ReadonlySet<HelpCenterHomeSection>
	>(() => new Set())

	const docsNavStore = useDocNavStore()
	useEffect(() => {
		docsNavStore.lastOpenedArticleId = ""
	}, [docsNavStore])

	const {
		publicArticles,
		getStartedArticles,
		popularArticles,
		curatedArticles,
		homeCandidates,
	} = useMemo(() => {
		const all = articleGroups.flatMap((group) => group.helpCenterArticles)
		const getStarted = articlesInHomeSection(all, "GET_STARTED")
		const popular = articlesInHomeSection(all, "POPULAR")
		return {
			publicArticles: all.filter(isPublicTreeRow),
			getStartedArticles: getStarted,
			popularArticles: popular,
			curatedArticles: [...getStarted, ...popular],
			homeCandidates: homeSectionCandidates(all),
		}
	}, [articleGroups])

	if (!workspace) {
		return null
	}

	const exampleQuestions = resolveExampleQuestions({
		base: workspace.aiChatSettings?.exampleQuestions,
		translated: workspace.aiChatSettingsTranslations?.find(
			(translation) => translation.language === selectedLanguage.code,
		)?.exampleQuestions,
	})

	const canEdit = offersDocsAddTile({ ready: articlesSettled, role })
	const hiddenCurated = hiddenCuratedSections(
		{ GET_STARTED: getStartedArticles, POPULAR: popularArticles },
		addedCurated,
	)
	const setCuratedAdded = (section: HelpCenterHomeSection, added: boolean) =>
		setAddedCurated((current) => {
			const next = new Set(current)
			if (added) next.add(section)
			else next.delete(section)
			return next
		})

	return (
		<div className="flex h-full flex-col">
			<HelpCenterHeader
				selectedLanguage={selectedLanguage}
				setSelectedLanguage={setSelectedLanguage}
			/>

			{/* items-start: stretched to the scroller's own height, the column's
			    bottom padding would land above the overflowing tiles and do nothing. */}
			<div className="no-scrollbar flex flex-grow flex-row items-start gap-10 overflow-y-auto pt-8">
				<div className="mx-auto flex max-w-full flex-1 flex-col px-5 pb-24 sm:px-10 lg:max-w-[56.76rem]">
					<HelpCenterGreeting
						suggestions={exampleQuestions}
						onSubmit={() => {
							toast.info({
								title: "Try this on your published docs",
								description:
									"Open it from the top right and turn on the Productlane Agent.",
							})
						}}
					/>
					{(["GET_STARTED", "POPULAR"] as const).map(
						(section) =>
							!hiddenCurated.includes(section) && (
								<HomeCuratedSection
									key={section}
									section={section}
									articles={
										section === "GET_STARTED"
											? getStartedArticles
											: popularArticles
									}
									candidates={homeCandidates}
									onRemoved={() => setCuratedAdded(section, false)}
								/>
							),
					)}
					<HomeCustomSections
						articleGroups={articleGroups}
						shownAbove={curatedArticles}
						candidates={homeCandidates}
						addableCurated={hiddenCurated}
						onAddCurated={(section) => setCuratedAdded(section, true)}
					/>
					{/* Readers find public articles in the tree, never as tiles on the home,
					    so this section only shows to start an empty docs site. */}
					{publicArticles.length === 0 && (
						<VisibilitySection
							label="Public"
							articles={publicArticles}
							emptyState={
								canEdit && (
									<>
										<DocsAddTile
											Icon={Globe}
											title="New public article"
											description="Lives on your docs site for customers to read."
											onClick={() => createArticle({ visibility: "PUBLIC" })}
										/>
										<DocsAddTile
											Icon={Import}
											title="Import from Markdown"
											description="Brings an existing help center over, folders and all."
											to={MARKDOWN_IMPORT_ROUTE}
										/>
									</>
								)
							}
						/>
					)}
				</div>
			</div>
		</div>
	)
}
